import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, DOCUMENT, inject } from '@angular/core';
import Bowser from 'bowser';
import { basicURLs } from 'src/brand/basic/basic.urls';
import { tascURLs } from 'src/brand/tasc/tasc.urls';

import { BrandId } from '../models/clux/enum';
import { InjectionTokens } from '../utils/injection-token';

@Injectable({
  providedIn: 'root',
})
export class BrowserService {
  private platformId = inject(PLATFORM_ID);
  private document = inject<Document>(DOCUMENT);
  private location = inject<Location>(InjectionTokens.LOCATION);
  private window = inject<Window>(InjectionTokens.WINDOW);

  private readonly _isRenderingInBrowser: boolean;

  constructor() {
    const platformId = this.platformId;

    this._isRenderingInBrowser = isPlatformBrowser(platformId);
  }

  public clearFocus(): void {
    if (!this._isRenderingInBrowser) {
      return;
    }

    const activeEl = this.document.activeElement as HTMLElement;
    if (activeEl && typeof activeEl.blur === 'function') {
      activeEl.blur();
    }
  }

  public getLocationHostname(): string {
    if (!this._isRenderingInBrowser) {
      return null;
    }
    if (Object.prototype.hasOwnProperty.call(this.window, 'Cypress')) {
      return this.getMockedLocationHostname();
    }

    return this.location.hostname;
  }

  public getLocationOrigin(): string {
    if (!this._isRenderingInBrowser) {
      return null;
    }

    return `${this.location.protocol}//${this.location.hostname}${this.location.port ? `:${this.location.port}` : ''}`;
  }

  public getLocationPathname(): string {
    if (!this._isRenderingInBrowser) {
      return null;
    }

    return this.location.pathname;
  }

  public isBrowserSupported(): boolean {
    if (!this._isRenderingInBrowser) {
      return true;
    }

    const browserEnvironment = Bowser.getParser(this.window.navigator.userAgent);
    return !browserEnvironment.satisfies({
      ie: '<12',
      safari: '<10',
    });
  }

  public redirectToPage(href: string): void {
    if (!this._isRenderingInBrowser) {
      return;
    }

    this.location.href = href;
  }

  /**
   * Return a mocked value for location.hostname. When the query string contains brand=BASIC,
   * return the prod URL for CBA. When it's missing or any other value, return the prod URL for UBA.
   * NOTE: We use this function ONLY when running a Cypress test, and we do it this way because
   * there does not appear to be a way to mock location.hostname from Cypress.
   */
  private getMockedLocationHostname(): string {
    const urlParams = new URLSearchParams(this.location.search);
    const brand = urlParams.get('brand') as BrandId;
    switch (brand) {
      case BrandId.BASIC:
        return basicURLs[basicURLs.length - 1];
      case BrandId.TASC:
      default:
        return tascURLs[tascURLs.length - 1];
    }
  }
}
